import { useCurrentClient } from '@pbk-web/state';
import { Button, ButtonSize, ButtonType } from '@pbk-web/ui';
import type { FC, JSX } from 'react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import type { ErrorStateProps } from '../../global-state/Errors';
import { useErrors } from '../../global-state/Errors';

const ErrorPage: FC<ErrorStateProps> = ({ ...props }): JSX.Element => {
  const location = useLocation();
  const { t } = useTranslation('common');
  const currentClient = useCurrentClient((x) => x.value);
  const setError = useErrors((x) => x.setValue);

  useEffect(() => {
    if (props.pageUrl !== location.pathname) {
      setError(null);
    }
  }, [location.pathname, props.pageUrl, setError]);

  const clientId = currentClient?.id;

  const goBackText = clientId ? t('errors.actions.go-back') : t('errors.actions.go-home');

  const { type, goBackCallback, showGoBack, showContactSupport, showRefresh } = {
    ...props,
  };

  const contactSupport = useCallback(() => {
    window.open('mailto:support@playbk.io', '_blank');
  }, []);

  if (type === null) {
    return <div>error</div>;
  }

  const buttonClasses = 'whitespace-nowrap flex flex-1 justify-center items-center';

  return (
    <div className="flex min-h-screen w-full flex-col items-center px-8 pt-44">
      <div className="text-pbk-48 text-text-2 mb-2 text-center font-medium">{t(`errors.${type}.title`)}</div>
      <div className="text-pbk-32 text-text-2 mb-5 text-center font-medium">{t(`errors.${type}.subtitle`)}</div>
      <div className="text-pbk-20 text-text-2 mb-20 text-center">{t(`errors.${type}.description`)}</div>
      <div className="flex space-x-4">
        {showGoBack && (
          <Button
            className={buttonClasses}
            size={ButtonSize.M}
            type={ButtonType.PRIMARY}
            onClick={() => {
              history.go(-1);
              if (goBackCallback) goBackCallback();
            }}
          >
            {goBackText}
          </Button>
        )}
        {showRefresh && (
          <Button className={buttonClasses} size={ButtonSize.M} type={ButtonType.PRIMARY} onClick={() => history.go(0)}>
            {t('errors.actions.refresh')}
          </Button>
        )}
        {showContactSupport && (
          <Button
            className={buttonClasses}
            size={ButtonSize.M}
            type={showContactSupport || showRefresh ? ButtonType.SECONDARY : ButtonType.PRIMARY}
            onClick={contactSupport}
          >
            {t('errors.actions.contact-support')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ErrorPage;
